import { useState, useEffect, useCallback } from 'react';
import { Lock, X, Loader2, Delete, CheckCircle } from 'lucide-react';

interface SecurityPinModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerify: (pin: string) => Promise<void>;
  title?: string;
  description?: string;
  isLoading?: boolean;
  error?: string | null;
}

const PIN_LENGTH = 4;

export default function SecurityPinModal({
  isOpen,
  onClose,
  onVerify,
  title = 'Security Verification',
  description = 'Enter your security PIN to proceed',
  isLoading = false,
  error = null,
}: SecurityPinModalProps) {
  const [pin, setPin] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setPin('');
      setLocalError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (error) {
      setLocalError(error);
      setPin('');
    }
  }, [error]);

  const handleNumberClick = useCallback((num: string) => {
    if (pin.length < 6 && !isLoading) {
      setLocalError(null);
      setPin((prev) => prev + num);
    }
  }, [pin.length, isLoading]);

  const handleDelete = useCallback(() => {
    if (!isLoading) {
      setPin((prev) => prev.slice(0, -1));
      setLocalError(null);
    }
  }, [isLoading]);

  const handleClear = useCallback(() => {
    if (!isLoading) {
      setPin('');
      setLocalError(null);
    }
  }, [isLoading]);

  const handleSubmit = useCallback(async () => {
    if (pin.length < PIN_LENGTH) {
      setLocalError(`PIN must be at least ${PIN_LENGTH} digits`);
      return;
    }
    try {
      await onVerify(pin);
    } catch {
      // Error handled by parent via error prop
    }
  }, [pin, onVerify]);

  useEffect(() => {
    if (!isOpen || isLoading) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') {
        handleNumberClick(e.key);
      } else if (e.key === 'Backspace') {
        handleDelete();
      } else if (e.key === 'Enter' && pin.length >= PIN_LENGTH) {
        handleSubmit();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isLoading, pin.length, handleNumberClick, handleDelete, handleSubmit, onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const numpadRows = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['clear', '0', 'delete'],
  ];

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-sm bg-white rounded-xl shadow-2xl transform transition-all">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="p-6 pb-4 text-center">
            <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
              <Lock className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          </div>

          <div className="px-6 pb-4">
            <div className="flex justify-center gap-3">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className={`w-10 h-12 rounded-lg border-2 flex items-center justify-center transition-all ${
                    i < pin.length
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  {i < pin.length ? (
                    <div className="w-3 h-3 bg-purple-600 rounded-full" />
                  ) : null}
                </div>
              ))}
            </div>
            {localError && (
              <div className="mt-3 text-center">
                <p className="text-sm text-red-600">{localError}</p>
              </div>
            )}
          </div>

          <div className="px-6 pb-6">
            <div className="grid grid-cols-3 gap-3">
              {numpadRows.map((row) =>
                row.map((key) => {
                  if (key === 'clear') {
                    return (
                      <button
                        key={key}
                        onClick={handleClear}
                        disabled={isLoading || pin.length === 0}
                        className="h-14 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Clear
                      </button>
                    );
                  }
                  if (key === 'delete') {
                    return (
                      <button
                        key={key}
                        onClick={handleDelete}
                        disabled={isLoading || pin.length === 0}
                        className="h-14 flex items-center justify-center text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Delete className="h-5 w-5" />
                      </button>
                    );
                  }
                  return (
                    <button
                      key={key}
                      onClick={() => handleNumberClick(key)}
                      disabled={isLoading || pin.length >= 6}
                      className="h-14 text-xl font-semibold text-gray-900 bg-gray-50 rounded-xl hover:bg-gray-100 active:bg-purple-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {key}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-xl border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading || pin.length < PIN_LENGTH}
              className="px-6 py-2.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Confirm
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
